<template>
  <div class="chart-round-chooser column">
    <input-checkbox
      v-if="mode == 'range'"
      :value="allRoundsSelected"
      :label="$t('labels.entireSimulation') + ` (${maxCompletedRounds})`"
      :disabled="selectedSimulations.length == 0"
      @update:value="toggleAllRoundsOption()"
    />
    <input-checkbox
      v-else-if="mode == 'pair'"
      :value="allRoundsSelected"
      :label="$t('labels.startAndEndRound')"
      :disabled="selectedSimulations.length == 0"
      @update:value="toggleAllRoundsOption()"
    />

    <template v-else-if="mode == 'list' || mode == 'single'">
      <input-checkbox
        :value="allRoundsSelected"
        :label="$t('labels.lastRound')"
        :disabled="selectedSimulations.length == 0"
        @update:value="toggleAllRoundsOption()"
      />
      <template v-if="allRoundsSelected">
        <chart-data-button
          :chart-uid="chartUid"
          item-type="simulationRound"
          item-id="last"
          @remove="toggleAllRoundsOption()"
        >
          {{ $t("labels.lastRound") }}
        </chart-data-button>
      </template>
    </template>

    <template v-if="!allRoundsSelected">
      <div v-if="mode == 'range' || mode == 'pair'" class="column">
        <input-dropdown
          :options="availableStartRounds"
          :value="startRound"
          :item-key="'index'"
          :label="
            mode == 'range' ? $t('labels.start') : $t('labels.startRound')
          "
          @update:value="setStartRound($event)"
        >
          <template #default="{ item }">
            <template v-if="item">
              {{ formatRoundIndex(item.index) }}
            </template>
          </template>
        </input-dropdown>
        <input-dropdown
          :options="availableEndRounds"
          :value="endRound"
          :item-key="'index'"
          :label="mode == 'range' ? $t('labels.end') : $t('labels.endRound')"
          @update:value="setEndRound($event)"
        >
          <template #default="{ item }">
            <template v-if="item">
              {{ formatRoundIndex(item.index) }}
            </template>
          </template>
        </input-dropdown>
      </div>

      <div v-else-if="mode == 'list'">
        <sortable-list
          :items="selectedRounds"
          :order="selectedRoundsOrder"
          :item-key="'index'"
          :sortable="true"
          @update:order="updateRoundOrder($event)"
        >
          <template #default="{ item: round }">
            <chart-data-button
              :chart-uid="chartUid"
              item-type="simulationRound"
              :item-id="round.index"
              @remove="removeRound(round)"
            >
              {{ formatRoundIndex(round.index) }}
            </chart-data-button>
          </template>

          <template #after-list>
            <input-dropdown
              :options="unselectedRounds"
              :item-key="'index'"
              :clear-on-select="true"
              @update:value="addRound($event)"
            >
              <template #default="{ item }">
                <template v-if="item">
                  {{ formatRoundIndex(item.index) }}
                </template>
              </template>
            </input-dropdown>
          </template>
        </sortable-list>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { ensureInt } from "@/helpers/utility/Type";

import { useChartData } from "./useChartData";
import { VIRTUAL_END_ROUND_INDEX, VIRTUAL_START_ROUND_INDEX } from "./ChartItem";

import InputDropdown from "@core/input/InputDropdown.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";
import SortableList from "@core/ui/SortableList.vue";
// import ButtonItem from "@core/input/ButtonItem.vue";

import ChartDataButton from "./ChartBuilderDataButton.vue";

import { formatRoundName } from "@editor/items/simulationRound/useRoundTime";
import { useCommonTimeUnits } from "@editor/items/simulation/useCommonTimeUnits";
import { useChartStore } from "./charts.store";
import { useEvaluationStore } from "@editor/items/evaluation/evaluations.store";

import type { Uid } from "@/helpers/utility/Id";

const props = defineProps<{
  chartUid: Uid<"evaluation" | "chart">;
}>();

const { t } = useI18n();

const store = computed(() => {
  if (props.chartUid.startsWith("chart")) {
    return useChartStore();
  } else {
    return useEvaluationStore();
  }
});

const chart = computed(() => {
  return store.value.byUid[props.chartUid];
});

const {
  selectedSimulations,
  allRoundOptions,
  maxCompletedRounds,
  chartAxisConfig
} = useChartData(chart);

const { roundLength, durationType, timeStart, roundUnit } =
  useCommonTimeUnits(selectedSimulations);

const mode = computed(() => {
  return chartAxisConfig.value?.roundMode || "range";
});
const allChosenRoundIds = computed(() => {
  return chart.value.data.simulationRound.map(ensureInt);
});
const allRoundsSelected = computed(() => {
  return chart.value.dataToggles.all.simulationRound;
});
const selectedRoundsOrder = computed(() => {
  if (allRoundsSelected.value) {
    return allRoundOptions.value.map((r) => r.index);
  }

  const selectedIds = [...allChosenRoundIds.value];
  const hasVirtualStartRound = selectedIds.includes(VIRTUAL_START_ROUND_INDEX);
  const hasVirtualEndRound = selectedIds.includes(VIRTUAL_END_ROUND_INDEX);
  const filteredSelectedIds = selectedIds.filter(
    (id) => id !== VIRTUAL_START_ROUND_INDEX && id !== VIRTUAL_END_ROUND_INDEX
  );

  let minRoundIndex = Math.min(...filteredSelectedIds) || 0;
  if (hasVirtualStartRound) {
    minRoundIndex = VIRTUAL_START_ROUND_INDEX;
  }
  const maxPossibleRoundIndex = allRoundOptions.value.length - 1;
  let maxRoundIndex = Math.min(
    Math.max(...filteredSelectedIds),
    maxPossibleRoundIndex
  );
  if (hasVirtualEndRound) {
    maxRoundIndex = maxPossibleRoundIndex;
  }

  if (mode.value == "range") {
    const order = allRoundOptions.value
      .filter((r) => r.index >= minRoundIndex && r.index <= maxRoundIndex)
      .map((r) => r.index);
    
    if (hasVirtualStartRound && !order.includes(VIRTUAL_START_ROUND_INDEX)) {
      order.unshift(VIRTUAL_START_ROUND_INDEX);
    }
    if (hasVirtualEndRound && !order.includes(VIRTUAL_END_ROUND_INDEX)) {
      order.push(VIRTUAL_END_ROUND_INDEX);
    }

    return order;
  }

  if (hasVirtualEndRound) {
    maxRoundIndex = VIRTUAL_END_ROUND_INDEX;
  }
  if (mode.value == "pair") {
    return [minRoundIndex, maxRoundIndex];
  }
  return selectedIds;
});
const orderedSelectedRounds = computed(() => {
  return selectedRoundsOrder.value.map((index) => ({ index }));
});
const selectedRounds = computed(() => {
  const roundsByIndex: { [index: string]: { index: number } } = {};

  for (const round of orderedSelectedRounds.value) {
    roundsByIndex[round.index] = round;
  }

  return roundsByIndex;
});
const availableStartRounds = computed(() => {
  if (endRound.value == null || endRound.value.index == VIRTUAL_END_ROUND_INDEX) {
    return allRoundOptions.value;
  }
  return allRoundOptions.value.filter(
    (r) => r.index <= (endRound.value?.index || 0)
  );
});
const availableEndRounds = computed(() => {
  return allRoundOptions.value.filter(
    (r) => r.index >= (startRound.value?.index || 0) || r.index === VIRTUAL_END_ROUND_INDEX
  );
});
const unselectedRounds = computed(() => {
  const selectedIndices = allChosenRoundIds.value;
  return allRoundOptions.value.filter(
    (r) => !selectedIndices.includes(r.index)
  );
});
const startRound = computed(() => {
  if (orderedSelectedRounds.value.length == 0) return null;
  return orderedSelectedRounds.value[0];
});
const endRound = computed(() => {
  if (orderedSelectedRounds.value.length == 0) return null;
  return orderedSelectedRounds.value[orderedSelectedRounds.value.length - 1];
});

function formatRoundIndex(roundIndex: number) {
  if (roundIndex == VIRTUAL_START_ROUND_INDEX) return t("labels.startState");
  if (roundIndex == VIRTUAL_END_ROUND_INDEX) return t("labels.endState");
  return formatRoundName(
    durationType.value,
    roundUnit.value,
    roundLength.value,
    roundIndex,
    timeStart.value
  );
}

function toggleAllRoundsOption() {
  if (!allRoundsSelected.value) {
    store.value.toggleAllOption(
      chart.value.id,
      "simulationRound"
    );
  } else {
    // Fill rounds array with currently selected round indices:
    if (mode.value == "range" || mode.value == "pair") {
      store.value.setRounds(chart.value.id, [
        startRound.value?.index || 0,
        endRound.value?.index || 0
      ]);
    } else {
      store.value.setRounds(chart.value.id, [
        endRound.value?.index || 0
      ]);
    }
  }
}

function setStartRound(round: { index: number }) {
  const containsVirtualEndRound = allChosenRoundIds.value.includes(VIRTUAL_END_ROUND_INDEX);
  const roundIds = allChosenRoundIds.value
    .filter((i) => i > round.index)
    .sort((a, b) => a - b);
  roundIds.unshift(round.index);
  if (containsVirtualEndRound) {
    roundIds.push(VIRTUAL_END_ROUND_INDEX);
  }
  store.value.setRounds(chart.value.id, roundIds);
}

function setEndRound(round: { index: number }) {
  if (round.index == VIRTUAL_END_ROUND_INDEX) {
    const roundIds = allChosenRoundIds.value
      .filter((i) => i >= (startRound.value?.index || 0))
      .sort((a, b) => a - b);
    roundIds.push(VIRTUAL_END_ROUND_INDEX);
    store.value.setRounds(chart.value.id, [roundIds[0], roundIds[roundIds.length - 1]]);
    return;
  }
  const roundIds = allChosenRoundIds.value
    .filter((i) => i < round.index && i != VIRTUAL_END_ROUND_INDEX)
    .sort((a, b) => a - b);
  roundIds.push(round.index);
  store.value.setRounds(chart.value.id, [roundIds[0], roundIds[roundIds.length - 1]]);
}

function addRound(round: { index: number }) {
  store.value.addRounds(chart.value.id, [round.index]);
}

function removeRound(round: { index: number }) {
  store.value.removeRounds(chart.value.id, [round.index]);
}

function updateRoundOrder(order: number[]) {
  store.value.updateItemOrder(
    chart.value.id,
    "simulationRound",
    order
  );
}
</script>

<style></style>
